import { useSession } from '@sdk/session/SessionContext';
import gettext from '@sdk/lang/gettext';

import TextEditor from '@web/richText/TextEditor';
import {
  ConversationFormWrapper,
  Separator,
  FieldWrapper,
} from '@web/conversations/ComposeConversation/composeConversation.styles';
import { CreateGroupConversationHeader } from '@web/conversations/CreateGroupConversation/CreateGroupConversationHeader/CreateGroupConversationHeader';
import {
  useCreateGroupConversation,
  useCreateGroupConversationRestrictions,
} from '@web/conversations/CreateGroupConversation/createGroupConversation.hooks';
import { ConversationNameField } from '@web/conversations/ComposeConversation/ConversationNameField/ConversationNameField';
import { ConversationMembersField } from '@web/conversations/ComposeConversation/ConversationMembersField/ConversationMembersField';
import { ConversationRestrictionsField } from '@web/conversations/ComposeConversation/ConversationRestrictionsField/ConversationRestrictionsField';
import { ComposeConversationActions } from '@web/conversations/ComposeConversation/ComposeConversationActions/ComposeConversationActions';

import { BulkModeSubmitConfirmation } from './components/BulkModeSubmitConfirmation/BulkModeSubmitConfirmation';
import { BulkModeSubmitError } from './components/BulkModeSubmitError/BulkModeSubmitError';
import { BulkModeSwitch } from './components/BulkModeSwitch/BulkModeSwitch';
import { useBulkMessageMode } from './hooks/useBulkMessageMode';
import { useBulkSubmitGuard } from './hooks/useBulkSubmitGuard';
import { FieldLabel } from './createGroupConversation.styles';

export const CreateGroupConversation = () => {
  useCreateGroupConversationRestrictions();
  const { currentUser } = useSession();
  const { bulkMessageEnabled, onBulkMessageToggle } = useBulkMessageMode();
  const {
    cancel,
    everyoneWorkingByDayOptions,
    fields,
    isDirty,
    isFormValid,
    submit,
    submitErrors,
    submitting,
    isNavigatingAway,
  } = useCreateGroupConversation(bulkMessageEnabled);
  const {
    guardedSubmit,
    hideSubmitGuard,
    isSubmiGuardModalVisible,
    recipientsCounter,
    shouldDisplayError,
    shouldDisplayWarning,
  } = useBulkSubmitGuard({
    bulkMessageEnabled,
    selectedOptions: fields.members.value,
    submit,
  });

  return (
    <ConversationFormWrapper>
      <CreateGroupConversationHeader />
      {!bulkMessageEnabled && <ConversationNameField name={fields.name} />}
      <ConversationMembersField
        members={fields.members}
        hasError={Boolean(submitErrors.length && fields.members.error)}
        extraPickerOptions={everyoneWorkingByDayOptions}
      />

      {currentUser.isManager && (
        <>
          <BulkModeSwitch isEnabled={bulkMessageEnabled} onChange={onBulkMessageToggle} />

          <Separator />

          {bulkMessageEnabled ? (
            <FieldWrapper>
              <FieldLabel>Message</FieldLabel>

              <TextEditor
                gifPickerEnabled={false}
                hasError={submitErrors.length && fields.content.error?.length > 0}
                onBlur={fields.content.onBlur}
                onContentChange={(content) => {
                  fields.attachments.onChange(content.attachments);
                  fields.content.onChange(content.text.trim());
                }}
                submitOnEnter={false}
              />
            </FieldWrapper>
          ) : (
            <ConversationRestrictionsField
              submitErrors={submitErrors}
              members={fields.members}
              restricted={fields.restricted}
              restrictedToUsers={fields.restrictedToUsers}
            />
          )}

          {shouldDisplayWarning && isSubmiGuardModalVisible && (
            <BulkModeSubmitConfirmation
              counter={recipientsCounter}
              onClose={hideSubmitGuard}
              onConfirm={() => {
                hideSubmitGuard();
                submit();

                return Promise.resolve(true);
              }}
            />
          )}

          {shouldDisplayError && isSubmiGuardModalVisible && (
            <BulkModeSubmitError onClose={hideSubmitGuard} />
          )}
        </>
      )}
      <ComposeConversationActions
        onCancel={cancel}
        onSubmit={() => {
          if (!submitting && !isNavigatingAway) {
            guardedSubmit();
          }
        }}
        isFormDirty={isDirty}
        isFormValid={isFormValid}
        submitLabel={bulkMessageEnabled ? gettext('Send') : gettext('Create')}
      />
    </ConversationFormWrapper>
  );
};
